import { defineMessage } from 'react-intl';
const PERPLEXITY_DEVELOPER = defineMessage({
    defaultMessage: 'Perplexity',
    id: '3da258847d',
    description: 'Developer name for connectors built by Perplexity',
});
const MERGE_DEVELOPER = defineMessage({
    defaultMessage: 'Merge',
    id: '525997e026',
    description: 'Developer name for connectors provided through Merge integration',
});
const CASHMERE_DEVELOPER = defineMessage({
    defaultMessage: 'Cashmere',
    id: '9572846880',
    description: 'Developer name for connectors provided through Cashmere integration',
});
const TIKTOK_DEVELOPER = defineMessage({
    defaultMessage: 'TikTok',
    id: '961cffa1b3',
    description: 'Developer name for the TikTok Ads connector',
});
const LINEAR_TAGLINE = defineMessage({
    defaultMessage: 'Plan and track projects, issues, and team workflows in Linear',
    id: '81ebc0468c',
    description: 'Tagline for the Linear connector shown in the connector setup modal',
});
const LINEAR_SEARCH = defineMessage({
    defaultMessage: 'Search across Linear issues, projects, and teams',
    id: '92a53484f2',
    description: 'Feature bullet point for the Linear connector',
});
const LINEAR_CREATE_AND_UPDATE = defineMessage({
    defaultMessage: 'Create and update Linear issues and projects',
    id: '334582f3c7',
    description: 'Feature bullet point for the Linear connector',
});
const PROJECT_MANAGEMENT_WORKFLOWS = defineMessage({
    defaultMessage: 'Track progress, plan cycles, and manage workflows',
    id: 'db805609b0',
    description: 'Feature bullet point for project management connectors',
});
const LINEAR_DATA_SYNC = defineMessage({
    defaultMessage: 'Data is retrieved from and written back to Linear whenever you query in Perplexity',
    id: 'c1b8c40fea',
    description: 'Data sync disclosure for the Linear connector',
});
const MERGE_SUBPROCESSOR_FOOTNOTE = defineMessage({
    defaultMessage: 'This connector is available through Merge API, Inc. By adding this connector, you agree that Merge will be a data sub-processor.',
    id: '27257c4642',
    description: 'Legal footnote about Merge as a data sub-processor for connectors',
});
const FILE_CATALOGUING = defineMessage({
    defaultMessage: 'Selected files and folders are catalogued for higher accuracy search results',
    id: '5e490e3851',
    description: 'Feature bullet point for file-based connectors about cataloguing',
});
const GOOGLE_DRIVE_PERMISSIONS = defineMessage({
    defaultMessage: 'File and folder selection is based on your existing Google Drive permissions',
    id: '3795276743',
    description: 'Feature bullet point for the Google Drive connector about permissions',
});
const CONTENT_MANAGEMENT_SEARCH = defineMessage({
    defaultMessage: 'Search across your pages and data',
    id: '1f9795e20e',
    description: 'Feature bullet point for content management connectors about searching',
});
const CONTENT_MANAGEMENT_PAGE_CREATION = defineMessage({
    defaultMessage: 'Create new pages in your teamspace from Perplexity',
    id: '16eb7d9d44',
    description: 'Feature bullet point for content management connectors about page creation',
});
const CONTENT_MANAGEMENT_DATA_UPDATE = defineMessage({
    defaultMessage: 'Update your data on existing pages directly from Perplexity',
    id: '5c869d16d2',
    description: 'Feature bullet point for content management connectors about updating data',
});
const ASANA_TAGLINE = defineMessage({
    defaultMessage: 'Plan and track projects, tasks, and team workflows in Asana',
    id: '36ad7aa5e4',
    description: 'Tagline for the Asana connector shown in the connector setup modal',
});
const ASANA_SEARCH = defineMessage({
    defaultMessage: 'Search across Asana tasks, projects, and teams',
    id: '1b1576d9eb',
    description: 'Feature bullet point for the Asana connector about searching',
});
const ASANA_CREATE_AND_UPDATE = defineMessage({
    defaultMessage: 'Create and update Asana tasks and projects',
    id: '2c7a4c1312',
    description: 'Feature bullet point for the Asana connector about task management',
});
const ASANA_DATA_SYNC = defineMessage({
    defaultMessage: 'Data is retrieved from and written back to Asana whenever you query in Perplexity',
    id: '868a6c8f27',
    description: 'Data sync disclosure for the Asana connector',
});
const EMAIL_AND_EVENT_MANAGEMENT = defineMessage({
    defaultMessage: 'Manage and monitor emails and events',
    id: 'f5ef48f052',
    description: 'Feature bullet point for email connectors about managing emails and events',
});
const CASHMERE_SUBPROCESSOR_FOOTNOTE = defineMessage({
    defaultMessage: 'This connector is available through Cashmere. By adding this connector, you agree that Cashmere will be a data sub-processor.',
    id: '9bfe033391',
    description: 'Legal footnote about Cashmere as a data sub-processor for connectors',
});
// Keyed on the connector source id (a plain string). The set of available
// connectors is server-driven, so an unknown id simply has no entry here and
// lookups return `undefined` — callers must handle the missing case.
export const CONNECTOR_DETAILS = {
    linear_native: {
        name: 'Linear',
        tagline: LINEAR_TAGLINE,
        bullets: [
            LINEAR_SEARCH,
            LINEAR_CREATE_AND_UPDATE,
            PROJECT_MANAGEMENT_WORKFLOWS,
            LINEAR_DATA_SYNC,
        ],
        developer: PERPLEXITY_DEVELOPER,
        websiteUrl: 'https://www.linear.app',
        documentationUrl: null,
        supportUrl: 'https://www.perplexity.ai/help-center/en/articles/12167711-linear-connector-for-enterprise-pro',
        footnote: null,
    },
    // @deprecated Replaced by `linear_native`. Retained so users with stale
    // `linear_alt` connections still see rich connector metadata in the UI.
    linear_alt: {
        name: 'Linear',
        tagline: LINEAR_TAGLINE,
        bullets: [
            LINEAR_SEARCH,
            LINEAR_CREATE_AND_UPDATE,
            PROJECT_MANAGEMENT_WORKFLOWS,
            LINEAR_DATA_SYNC,
        ],
        developer: MERGE_DEVELOPER,
        websiteUrl: 'https://www.linear.app',
        documentationUrl: null,
        supportUrl: 'https://www.perplexity.ai/help-center/en/articles/12167711-linear-connector-for-enterprise-pro',
        footnote: {
            link: 'https://www.perplexity.ai/help-center/en/articles/12167711-linear-connector-for-enterprise-pro',
            text: MERGE_SUBPROCESSOR_FOOTNOTE,
        },
    },
    google_drive: {
        name: 'Google Drive',
        tagline: defineMessage({
            defaultMessage: 'Get in-depth answers from your Google Drive content',
            id: '9d42f88d26',
            description: 'Tagline for the Google Drive connector shown in the connector setup modal',
        }),
        bullets: [FILE_CATALOGUING, GOOGLE_DRIVE_PERMISSIONS],
        consumerBullets: [
            defineMessage({
                defaultMessage: 'Attach files from Google Drive to your query',
                id: 'd859549dfb',
                description: 'Consumer-facing feature bullet for the Google Drive connector',
            }),
        ],
        connectedBullets: [
            GOOGLE_DRIVE_PERMISSIONS,
            defineMessage({
                defaultMessage: 'Opt into High-Precision Search for even more comprehensive answers',
                id: '89c83eb56c',
                description: 'Feature bullet for the Google Drive connector when already connected',
            }),
        ],
        developer: PERPLEXITY_DEVELOPER,
        websiteUrl: 'https://drive.google.com',
        supportUrl: 'https://www.perplexity.ai/help-center/en/articles/12870620-connecting-perplexity-with-google-drive',
        documentationUrl: null,
        footnote: null,
    },
    sharepoint: {
        name: 'SharePoint',
        tagline: defineMessage({
            defaultMessage: 'Get in-depth answers from your SharePoint content',
            id: 'e02f770b70',
            description: 'Tagline for the SharePoint connector shown in the connector setup modal',
        }),
        bullets: [
            FILE_CATALOGUING,
            defineMessage({
                defaultMessage: 'File and folder selection is based on your existing SharePoint permissions',
                id: 'f2231dea17',
                description: 'Feature bullet point for the SharePoint connector about permissions',
            }),
        ],
        developer: PERPLEXITY_DEVELOPER,
        websiteUrl: 'https://microsoft.sharepoint.com/',
        supportUrl: 'https://www.perplexity.ai/help-center/en/articles/12569435-how-to-use-the-microsoft-sharepoint-connector',
        documentationUrl: null,
        footnote: null,
    },
    onedrive: {
        name: 'OneDrive',
        tagline: defineMessage({
            defaultMessage: 'Get in-depth answers from your OneDrive content',
            id: '0b7f57c231',
            description: 'Tagline for the OneDrive connector shown in the connector setup modal',
        }),
        bullets: [
            FILE_CATALOGUING,
            defineMessage({
                defaultMessage: 'File and folder selection is based on your existing OneDrive permissions',
                id: '88b9453dfc',
                description: 'Feature bullet point for the OneDrive connector about permissions',
            }),
        ],
        developer: PERPLEXITY_DEVELOPER,
        websiteUrl: 'https://www.microsoft.com/en-us/microsoft-365/onedrive/online-cloud-storage',
        documentationUrl: null,
        supportUrl: 'https://www.perplexity.ai/help-center/en/articles/13014066-microsoft-onedrive-connector',
        footnote: null,
    },
    box: {
        name: 'Box',
        tagline: defineMessage({
            defaultMessage: 'Get in-depth answers from your Box content',
            id: '305500610f',
            description: 'Tagline for the Box connector shown in the connector setup modal',
        }),
        bullets: [
            FILE_CATALOGUING,
            defineMessage({
                defaultMessage: 'File and folder selection is based on your existing Box permissions',
                id: '0dfbfa08d1',
                description: 'Feature bullet point for the Box connector about permissions',
            }),
        ],
        developer: PERPLEXITY_DEVELOPER,
        websiteUrl: 'http://www.box.com/',
        documentationUrl: null,
        supportUrl: 'https://www.perplexity.ai/help-center/en/articles/13130932-using-the-box-connector',
        footnote: null,
    },
    dropbox: {
        name: 'Dropbox',
        tagline: defineMessage({
            defaultMessage: 'Get in-depth answers from your Dropbox content',
            id: '91f3fa66bc',
            description: 'Tagline for the Dropbox connector shown in the connector setup modal',
        }),
        bullets: [
            FILE_CATALOGUING,
            defineMessage({
                defaultMessage: 'File and folder selection is based on your existing Dropbox permissions',
                id: '9b6a6e533d',
                description: 'Feature bullet point for the Dropbox connector about permissions',
            }),
        ],
        consumerBullets: [
            defineMessage({
                defaultMessage: 'Attach files from Dropbox to your query',
                id: '46f58b90d3',
                description: 'Consumer-facing feature bullet for the Dropbox connector',
            }),
        ],
        developer: PERPLEXITY_DEVELOPER,
        websiteUrl: 'http://www.dropbox.com/',
        documentationUrl: null,
        supportUrl: 'https://www.perplexity.ai/help-center/en/articles/13060839-using-the-dropbox-connector',
        footnote: null,
    },
    notion_mcp: {
        name: 'Notion',
        tagline: defineMessage({
            defaultMessage: 'Search and create content on your Notion pages',
            id: '1b272a3b78',
            description: 'Tagline for the Notion connector shown in the connector setup modal',
        }),
        bullets: [
            CONTENT_MANAGEMENT_SEARCH,
            CONTENT_MANAGEMENT_PAGE_CREATION,
            CONTENT_MANAGEMENT_DATA_UPDATE,
            defineMessage({
                defaultMessage: 'Data is retrieved from and written back to Notion whenever you query in Perplexity',
                id: 'c51ae78359',
                description: 'Data sync disclosure for the Notion connector',
            }),
        ],
        developer: PERPLEXITY_DEVELOPER,
        websiteUrl: 'http://www.notion.so/',
        documentationUrl: null,
        supportUrl: 'https://www.perplexity.ai/help-center/en/articles/12167654-notion-connector-for-enterprise-pro',
        footnote: null,
    },
    github_mcp_direct: {
        name: 'GitHub',
        tagline: defineMessage({
            defaultMessage: 'Search and manage your GitHub repositories',
            id: '918567e3a3',
            description: 'Tagline for the GitHub connector shown in the connector setup modal',
        }),
        bullets: [
            defineMessage({
                defaultMessage: 'Search, analyze, and summarize your repositories and issues',
                id: 'd2acb0cba3',
                description: 'Feature bullet point for the GitHub connector about repo search',
            }),
            defineMessage({
                defaultMessage: 'Create, update, and manage issues and pull requests',
                id: '3516e9fafa',
                description: 'Feature bullet point for the GitHub connector about issue management',
            }),
            defineMessage({
                defaultMessage: 'Monitor workflows',
                id: '4a3874def3',
                description: 'Feature bullet point for the GitHub connector about workflow monitoring',
            }),
            defineMessage({
                defaultMessage: 'Search and manage notifications to streamline communication',
                id: 'efeac22bcc',
                description: 'Feature bullet point for the GitHub connector about notifications',
            }),
            defineMessage({
                defaultMessage: 'Data is retrieved from and written back to GitHub whenever you query in Perplexity',
                id: 'b911f85fbf',
                description: 'Data sync disclosure for the GitHub connector',
            }),
        ],
        developer: PERPLEXITY_DEVELOPER,
        websiteUrl: 'https://github.com/',
        supportUrl: 'https://www.perplexity.ai/help-center/en/articles/12275669-github-connector-for-enterprise',
        documentationUrl: null,
        footnote: null,
    },
    tiktok: {
        name: 'TikTok Ads',
        tagline: defineMessage({
            defaultMessage: 'Create, manage, and analyze advertising campaigns on TikTok',
            id: 'e8de9dfc0d',
            description: 'Tagline for the TikTok Ads connector shown in the connector setup modal',
        }),
        bullets: [
            defineMessage({
                defaultMessage: 'Review advertisers, campaigns, ad groups, ads, and performance reports',
                id: '4dd97123e1',
                description: 'Feature bullet point for the TikTok Ads connector about reporting',
            }),
            defineMessage({
                defaultMessage: 'Create and update campaigns, targeting, budgets, and creative assets',
                id: '6fbf933f6c',
                description: 'Feature bullet point for the TikTok Ads connector about campaign management',
            }),
            defineMessage({
                defaultMessage: 'Actions run through TikTok for Business MCP using your existing TikTok Ads permissions',
                id: '12c52cd75a',
                description: 'Data access disclosure for the TikTok Ads connector',
            }),
        ],
        developer: TIKTOK_DEVELOPER,
        websiteUrl: 'https://ads.tiktok.com/',
        documentationUrl: 'https://ads.tiktok.com/gateway/docs/index?doc_id=1751443956638721&language=ENGLISH',
        supportUrl: null,
        footnote: null,
    },
    asana_mcp_direct: {
        name: 'Asana',
        tagline: ASANA_TAGLINE,
        bullets: [ASANA_SEARCH, ASANA_CREATE_AND_UPDATE, PROJECT_MANAGEMENT_WORKFLOWS, ASANA_DATA_SYNC],
        developer: PERPLEXITY_DEVELOPER,
        websiteUrl: 'https://asana.com/',
        documentationUrl: null,
        supportUrl: 'https://www.perplexity.ai/help-center/en/articles/12524801-connecting-perplexity-with-asana',
        footnote: null,
    },
    asana_mcp_merge: {
        name: 'Asana',
        tagline: ASANA_TAGLINE,
        bullets: [ASANA_SEARCH, ASANA_CREATE_AND_UPDATE, PROJECT_MANAGEMENT_WORKFLOWS, ASANA_DATA_SYNC],
        developer: MERGE_DEVELOPER,
        websiteUrl: 'https://asana.com/',
        documentationUrl: null,
        supportUrl: 'https://www.perplexity.ai/help-center/en/articles/12524801-connecting-perplexity-with-asana',
        footnote: {
            link: 'https://www.perplexity.ai/help-center/en/articles/12524801-connecting-perplexity-with-asana',
            text: MERGE_SUBPROCESSOR_FOOTNOTE,
        },
    },
    atlassian_mcp_direct: {
        name: 'Atlassian',
        tagline: defineMessage({
            defaultMessage: 'Plan and track projects, tasks, and team workflows in Atlassian',
            id: '0d76d17e90',
            description: 'Tagline for the Atlassian connector shown in the connector setup modal',
        }),
        bullets: [
            defineMessage({
                defaultMessage: 'Search across Atlassian tasks, projects, and teams',
                id: 'c291a24d85',
                description: 'Feature bullet point for the Atlassian connector about searching',
            }),
            defineMessage({
                defaultMessage: 'Create and update Atlassian tasks and projects',
                id: 'd7855f5f92',
                description: 'Feature bullet point for the Atlassian connector about task management',
            }),
            PROJECT_MANAGEMENT_WORKFLOWS,
            defineMessage({
                defaultMessage: 'Data is retrieved from and written back to Atlassian whenever you query in Perplexity',
                id: '4488c6f344',
                description: 'Data sync disclosure for the Atlassian connector',
            }),
        ],
        developer: PERPLEXITY_DEVELOPER,
        websiteUrl: 'https://www.atlassian.com/',
        documentationUrl: null,
        supportUrl: 'https://www.perplexity.ai/help-center/en/collections/15347354-app-connectors',
        footnote: null,
    },
    jira_mcp_merge: {
        name: 'Jira',
        tagline: defineMessage({
            defaultMessage: 'Plan and track projects, tasks, and team workflows in Jira',
            id: '837acec192',
            description: 'Tagline for the Jira connector shown in the connector setup modal',
        }),
        bullets: [
            defineMessage({
                defaultMessage: 'Search across Jira tasks, projects, and teams',
                id: '9625a89b5e',
                description: 'Feature bullet point for the Jira connector about searching',
            }),
            defineMessage({
                defaultMessage: 'Create and update Jira tasks and projects',
                id: '03e40eb703',
                description: 'Feature bullet point for the Jira connector about task management',
            }),
            PROJECT_MANAGEMENT_WORKFLOWS,
            defineMessage({
                defaultMessage: 'Data is retrieved from and written back to Jira whenever you query in Perplexity',
                id: 'c634c60cc8',
                description: 'Data sync disclosure for the Jira connector',
            }),
        ],
        developer: MERGE_DEVELOPER,
        websiteUrl: 'https://www.atlassian.com/',
        documentationUrl: null,
        supportUrl: 'https://www.perplexity.ai/help-center/en/articles/12524825-connecting-perplexity-with-jira',
        footnote: {
            link: 'https://www.perplexity.ai/help-center/en/articles/12524825-connecting-perplexity-with-jira',
            text: MERGE_SUBPROCESSOR_FOOTNOTE,
        },
    },
    confluence_mcp_merge: {
        name: 'Confluence',
        tagline: defineMessage({
            defaultMessage: 'Search and create content on your Confluence pages',
            id: '756815d3d0',
            description: 'Tagline for the Confluence connector shown in the connector setup modal',
        }),
        bullets: [
            CONTENT_MANAGEMENT_SEARCH,
            CONTENT_MANAGEMENT_PAGE_CREATION,
            CONTENT_MANAGEMENT_DATA_UPDATE,
            defineMessage({
                defaultMessage: 'Data is retrieved from and written back to Confluence whenever you query in Perplexity',
                id: 'f9db8f8122',
                description: 'Data sync disclosure for the Confluence connector',
            }),
        ],
        developer: MERGE_DEVELOPER,
        websiteUrl: 'https://www.atlassian.com/',
        documentationUrl: null,
        supportUrl: 'https://www.perplexity.ai/help-center/en/articles/12524852-connecting-perplexity-with-confluence',
        footnote: {
            link: 'https://www.perplexity.ai/help-center/en/articles/12524852-connecting-perplexity-with-confluence',
            text: MERGE_SUBPROCESSOR_FOOTNOTE,
        },
    },
    microsoft_teams_mcp_merge: {
        name: 'Microsoft Teams',
        tagline: defineMessage({
            defaultMessage: 'Search and send messages in Microsoft Teams',
            id: 'e087a8f3e0',
            description: 'Tagline for the Microsoft Teams connector shown in the connector setup modal',
        }),
        bullets: [
            defineMessage({
                defaultMessage: 'Search across Microsoft Teams messages and channels',
                id: '08e990a636',
                description: 'Feature bullet point for the Microsoft Teams connector about searching',
            }),
            defineMessage({
                defaultMessage: 'Send and receive messages in Microsoft Teams',
                id: 'b85bb591be',
                description: 'Feature bullet point for the Microsoft Teams connector about messaging',
            }),
            defineMessage({
                defaultMessage: 'Data is retrieved from and written back to Microsoft Teams whenever you query in Perplexity',
                id: '3f802165dc',
                description: 'Data sync disclosure for the Microsoft Teams connector',
            }),
        ],
        developer: MERGE_DEVELOPER,
        websiteUrl: 'https://teams.microsoft.com',
        documentationUrl: null,
        supportUrl: 'https://www.perplexity.ai/help-center/en/articles/12674820-microsoft-teams-connector',
        footnote: {
            link: 'https://www.perplexity.ai/help-center/en/articles/12674820-microsoft-teams-connector',
            text: MERGE_SUBPROCESSOR_FOOTNOTE,
        },
    },
    slack_direct: {
        name: 'Slack',
        tagline: defineMessage({
            defaultMessage: 'Search and post messages across your Slack workspace',
            id: 'e5a8b8b9db',
            description: 'Tagline for the Slack connector shown in the connector setup modal',
        }),
        bullets: [
            defineMessage({
                defaultMessage: 'Search messages across DMs, private channels, and public channels you have access to in Slack',
                id: '748bb645eb',
                description: 'Feature bullet point for the Slack connector about message searching',
            }),
            defineMessage({
                defaultMessage: 'Post messages directly to Slack',
                id: '77202951b4',
                description: 'Feature bullet point for the Slack connector about posting messages',
            }),
            defineMessage({
                defaultMessage: 'Data is retrieved from and written back to Slack whenever you run a query in Perplexity',
                id: '442e68f50b',
                description: 'Data sync disclosure for the Slack connector',
            }),
        ],
        developer: PERPLEXITY_DEVELOPER,
        websiteUrl: 'https://slack.com',
        supportUrl: 'https://www.perplexity.ai/help-center/en/articles/12167980-using-the-connector-for-slack',
        documentationUrl: null,
        footnote: null,
    },
    gcal: {
        name: 'Gmail with Calendar',
        tagline: defineMessage({
            defaultMessage: 'Search, create, and manage your emails and calendar events',
            id: '5c7767710b',
            description: 'Tagline for the Gmail with Calendar connector shown in the connector setup modal',
        }),
        bullets: [
            defineMessage({
                defaultMessage: 'Search your Gmail and calendar',
                id: '846c54c542',
                description: 'Feature bullet point for the Gmail connector about searching',
            }),
            defineMessage({
                defaultMessage: 'Draft and send emails',
                id: '10c6f3a51d',
                description: 'Feature bullet point for the Gmail connector about email composition',
            }),
            EMAIL_AND_EVENT_MANAGEMENT,
            defineMessage({
                defaultMessage: 'Create and update calendar events',
                id: '8e36a68711',
                description: 'Feature bullet point for the Gmail connector about calendar management',
            }),
            defineMessage({
                defaultMessage: 'Data is retrieved from and written back to Gmail and Gcal whenever you query in Perplexity',
                id: '864b5ab347',
                description: 'Data sync disclosure for the Gmail with Calendar connector',
            }),
        ],
        developer: PERPLEXITY_DEVELOPER,
        websiteUrl: 'https://accounts.google.com/InteractiveLogin?emr=1&ltmpl=default&nojavascript=1&rm=false&service=mail&ss=1',
        documentationUrl: null,
        supportUrl: 'https://www.perplexity.ai/help-center/en/articles/12168040-connecting-perplexity-with-gmail-and-google-calendar',
        footnote: null,
    },
    outlook: {
        name: 'Outlook',
        tagline: defineMessage({
            defaultMessage: 'Search your emails and calendar events',
            id: '0446913b04',
            description: 'Tagline for the Outlook connector shown in the connector setup modal',
        }),
        bullets: [
            defineMessage({
                defaultMessage: 'Search your Outlook e-mail and calendar',
                id: 'bc6080d352',
                description: 'Feature bullet point for the Outlook connector about searching',
            }),
            EMAIL_AND_EVENT_MANAGEMENT,
            defineMessage({
                defaultMessage: 'Data is retrieved from and written back to Outlook whenever you query in Perplexity',
                id: 'a2dd727941',
                description: 'Data sync disclosure for the Outlook connector',
            }),
        ],
        developer: PERPLEXITY_DEVELOPER,
        websiteUrl: 'https://www.microsoft.com/en-us/microsoft-365/outlook/log-in',
        documentationUrl: null,
        supportUrl: 'https://www.perplexity.ai/help-center/en/articles/12301355-connecting-perplexity-with-outlook-com',
        footnote: null,
    },
    zoom: {
        name: 'Zoom',
        tagline: defineMessage({
            defaultMessage: 'Create and manage your Zoom meetings',
            id: '40a0d9156e',
            description: 'Tagline for the Zoom connector shown in the connector setup modal',
        }),
        bullets: [
            defineMessage({
                defaultMessage: 'Create and schedule Zoom meetings',
                id: '9f2ac7b0f0',
                description: 'Feature bullet point for the Zoom connector about meeting creation',
            }),
            defineMessage({
                defaultMessage: 'Manage your Zoom meeting settings',
                id: '830f114bdd',
                description: 'Feature bullet point for the Zoom connector about settings management',
            }),
            defineMessage({
                defaultMessage: 'Data is retrieved from and written back to Zoom whenever you query in Perplexity',
                id: 'ce3722a1f4',
                description: 'Data sync disclosure for the Zoom connector',
            }),
        ],
        developer: PERPLEXITY_DEVELOPER,
        websiteUrl: 'https://zoom.us',
        documentationUrl: null,
        supportUrl: null,
        footnote: null,
    },
    factset: {
        name: 'FactSet',
        tagline: defineMessage({
            defaultMessage: 'Access financial data and research from FactSet',
            id: '3d0305c821',
            description: 'Tagline for the FactSet connector shown in the connector setup modal',
        }),
        bullets: [],
        developer: PERPLEXITY_DEVELOPER,
        websiteUrl: 'https://www.factset.com',
        documentationUrl: null,
        supportUrl: null,
        footnote: null,
    },
    crunchbase: {
        name: 'Crunchbase',
        tagline: defineMessage({
            defaultMessage: 'Access company and startup data from Crunchbase',
            id: '0ba25f8b26',
            description: 'Tagline for the Crunchbase connector shown in the connector setup modal',
        }),
        bullets: [],
        developer: PERPLEXITY_DEVELOPER,
        websiteUrl: 'https://www.crunchbase.com',
        documentationUrl: null,
        supportUrl: null,
        footnote: null,
    },
    wiley: {
        name: 'Wiley',
        tagline: defineMessage({
            defaultMessage: 'Access academic research and publications from Wiley',
            id: '012702d6ee',
            description: 'Tagline for the Wiley connector shown in the connector setup modal',
        }),
        bullets: [],
        developer: PERPLEXITY_DEVELOPER,
        websiteUrl: 'https://www.wiley.com',
        documentationUrl: null,
        supportUrl: null,
        footnote: null,
    },
    edgar: {
        // deprecated
        name: 'EDGAR',
        tagline: defineMessage({
            defaultMessage: 'Access SEC filings and corporate reports',
            id: '8cb36bcd36',
            description: 'Tagline for the EDGAR connector shown in the connector setup modal',
        }),
        bullets: [],
        developer: PERPLEXITY_DEVELOPER,
        websiteUrl: 'https://www.sec.gov/edgar',
        documentationUrl: null,
        supportUrl: null,
        footnote: null,
    },
    linear: {
        name: 'Linear',
        tagline: defineMessage({
            defaultMessage: 'Get in-depth answers from your Linear content',
            id: 'a1c3838b2c',
            description: 'Tagline for the legacy Linear connector shown in the connector setup modal',
        }),
        bullets: [],
        developer: PERPLEXITY_DEVELOPER,
        websiteUrl: 'https://www.linear.app',
        documentationUrl: null,
        supportUrl: null,
        footnote: null,
    },
    slack: {
        name: 'Slack',
        tagline: defineMessage({
            defaultMessage: 'Get in-depth answers from your Slack content',
            id: 'd93ffadc4c',
            description: 'Tagline for the legacy Slack connector shown in the connector setup modal',
        }),
        bullets: [],
        developer: PERPLEXITY_DEVELOPER,
        websiteUrl: 'https://slack.com',
        documentationUrl: null,
        supportUrl: null,
        footnote: null,
    },
    wiley_mcp_cashmere: {
        name: 'Wiley',
        tagline: defineMessage({
            defaultMessage: 'Access academic research and publications from Wiley',
            id: '5ed3f2985e',
            description: 'Tagline for the Wiley (Cashmere) connector shown in the connector setup modal',
        }),
        bullets: [],
        developer: CASHMERE_DEVELOPER,
        websiteUrl: 'https://www.wiley.com',
        documentationUrl: null,
        supportUrl: null,
        footnote: {
            link: 'https://www.perplexity.ai/help-center',
            text: CASHMERE_SUBPROCESSOR_FOOTNOTE,
        },
    },
    cbinsights_mcp_cashmere: {
        name: 'CB Insights',
        tagline: defineMessage({
            defaultMessage: 'Access market intelligence and company data',
            id: 'ef60cc2c15',
            description: 'Tagline for the CB Insights connector shown in the connector setup modal',
        }),
        bullets: [],
        developer: CASHMERE_DEVELOPER,
        websiteUrl: 'https://www.cbinsights.com',
        documentationUrl: null,
        supportUrl: null,
        footnote: {
            link: 'https://www.perplexity.ai/help-center',
            text: CASHMERE_SUBPROCESSOR_FOOTNOTE,
        },
    },
    pitchbook_mcp_cashmere: {
        name: 'PitchBook',
        tagline: defineMessage({
            defaultMessage: 'Access private capital market data',
            id: 'b32c727ddd',
            description: 'Tagline for the PitchBook connector shown in the connector setup modal',
        }),
        bullets: [],
        developer: CASHMERE_DEVELOPER,
        websiteUrl: 'https://www.pitchbook.com',
        documentationUrl: null,
        supportUrl: null,
        footnote: {
            link: 'https://www.perplexity.ai/help-center',
            text: CASHMERE_SUBPROCESSOR_FOOTNOTE,
        },
    },
    statista_mcp_cashmere: {
        name: 'Statista',
        tagline: defineMessage({
            defaultMessage: 'Access market and consumer data',
            id: '14cffbbfbc',
            description: 'Tagline for the Statista connector shown in the connector setup modal',
        }),
        bullets: [],
        developer: CASHMERE_DEVELOPER,
        websiteUrl: 'https://www.statista.com',
        documentationUrl: null,
        supportUrl: null,
        footnote: {
            link: 'https://www.perplexity.ai/help-center',
            text: CASHMERE_SUBPROCESSOR_FOOTNOTE,
        },
    },
    apple_healthkit: {
        name: 'Apple Health',
        tagline: defineMessage({
            defaultMessage: 'Connect Apple Health to get personalized health and fitness insights',
            id: '59e99b8f7c',
            description: 'Tagline for the Apple Health connector shown in the connector setup modal',
        }),
        bullets: [
            defineMessage({
                defaultMessage: 'Securely connect health and fitness data from Apple Health',
                id: '0fe2d83702',
                description: 'Feature bullet point for the Apple Health connector about data access',
            }),
            defineMessage({
                defaultMessage: 'Ask questions about trends in activity, sleep, heart rate, vitals, and other shared metrics',
                id: 'f49ed209ee',
                description: 'Feature bullet point for the Apple Health connector about metric trends',
            }),
            defineMessage({
                defaultMessage: 'Get personalized answers powered by the data you choose to share',
                id: '38ac585318',
                description: 'Feature bullet point for the Apple Health connector about personalized answers',
            }),
            defineMessage({
                defaultMessage: 'Manage or revoke access from iOS settings at any time',
                id: 'c8b148e0dc',
                description: 'Feature bullet point for the Apple Health connector about access management',
            }),
        ],
        developer: defineMessage({
            defaultMessage: 'Apple',
            id: '805be7b67a',
            description: 'Developer name for the Apple Health connector',
        }),
        websiteUrl: 'https://www.apple.com/health/',
        supportUrl: 'https://www.perplexity.ai/help-center/en/articles/14035438-what-is-perplexity-health',
        documentationUrl: null,
        footnote: null,
    },
    plaid: {
        name: 'Plaid',
        tagline: defineMessage({
            defaultMessage: 'View your investment holdings, transactions, and liabilities. Powered by Plaid.',
            id: '4aa54700fa',
            description: 'Tagline for the Plaid connector shown in the connector setup modal',
        }),
        bullets: [
            defineMessage({
                defaultMessage: 'See holdings and balances from your connected brokerage accounts',
                id: '7ec730a75c',
                description: 'Feature bullet point for the Plaid connector about portfolio access',
            }),
            defineMessage({
                defaultMessage: 'Track transactions across your linked financial accounts',
                id: 'd84bfa87ea',
                description: 'Feature bullet point for the Plaid connector about transaction tracking',
            }),
            defineMessage({
                defaultMessage: 'View liabilities including loans, credit cards, and mortgages',
                id: 'abe96546bf',
                description: 'Feature bullet point for the Plaid connector about liabilities',
            }),
            defineMessage({
                defaultMessage: 'Get AI-powered insights about your finances',
                id: 'f82968996d',
                description: 'Feature bullet point for the Plaid connector about AI insights',
            }),
        ],
        developer: defineMessage({
            defaultMessage: 'Perplexity & Plaid',
            id: 'c215055b76',
            description: 'Developer name for the Plaid connector',
        }),
        websiteUrl: 'https://perplexity.ai/finance/portfolio',
        documentationUrl: null,
        supportUrl: 'https://www.perplexity.ai/hub/helpcenter',
        footnote: {
            link: 'https://www.perplexity.ai/hub/blog/plaid-integration-provides-full-view-of-personal-finances',
            text: defineMessage({
                defaultMessage: 'Learn more about the Plaid integration',
                id: 'f4b6a720ad',
                description: 'Footnote linking to the Plaid integration blog post',
            }),
        },
    },
    medical_records: {
        name: 'Medical Records',
        tagline: defineMessage({
            defaultMessage: 'Get personalized answers and plans using your health records',
            id: 'dcd48ef9b4',
            description: 'Tagline for the Medical Records connector shown in the connector setup modal',
        }),
        bullets: [
            defineMessage({
                defaultMessage: 'Securely connect your medical records from over 1M providers across the US',
                id: '540f8be09c',
                description: 'Feature bullet point for the Medical Records connector about data access',
            }),
            defineMessage({
                defaultMessage: 'Get personalized health answers using your lab results, medications, visit notes, and more',
                id: '372cd6b615',
                description: 'Feature bullet point for the Medical Records connector about health insights',
            }),
            defineMessage({
                defaultMessage: 'Works seamlessly across Computer and Search to help you explore your data in depth',
                id: 'ae01ec529b',
                description: 'Feature bullet point for the Medical Records connector about cross-product integration',
            }),
        ],
        developer: PERPLEXITY_DEVELOPER,
        websiteUrl: 'https://perplexity.ai/health',
        documentationUrl: null,
        supportUrl: 'https://www.perplexity.ai/help-center/en/articles/14035438-what-is-perplexity-health',
        footnote: null,
    },
    wearables: {
        name: 'Health Devices and Apps',
        tagline: defineMessage({
            defaultMessage: 'Securely connect your health apps and wearables for personalized health insights',
            id: 'dffeff186d',
            description: 'Tagline for the Health Devices and Apps connector shown in the connector setup modal',
        }),
        bullets: [
            defineMessage({
                defaultMessage: 'Securely connect your health apps and wearables including Fitbit, Ultrahuman, Withings, Clue, and more',
                id: 'fcb26cd212',
                description: 'Feature bullet point for the Health Devices and Apps connector about supported devices',
            }),
            defineMessage({
                defaultMessage: 'Track trends and visualize your data in a personalized health dashboard',
                id: 'f38c8a9d91',
                description: 'Feature bullet point for the Health Devices and Apps connector about tracking trends',
            }),
            defineMessage({
                defaultMessage: 'Get tailored insights and answers powered by your fitness and health data',
                id: '854f04da9a',
                description: 'Feature bullet point for the Health Devices and Apps connector about tailored insights',
            }),
            defineMessage({
                defaultMessage: 'Works seamlessly across Computer and Search to help you explore your data in depth',
                id: '77d8f2b058',
                description: 'Feature bullet point for the Health Devices and Apps connector about cross-product integration',
            }),
        ],
        developer: PERPLEXITY_DEVELOPER,
        websiteUrl: 'https://perplexity.ai/health',
        documentationUrl: null,
        supportUrl: 'https://www.perplexity.ai/help-center/en/articles/14035438-what-is-perplexity-health',
        footnote: null,
    },
    function_health: {
        name: 'Function Health',
        tagline: defineMessage({
            defaultMessage: 'Connect your Function Health account to get personalized health insights and recommendations',
            id: '67676bf403',
            description: 'Tagline for the Function Health connector shown in the connector setup modal',
        }),
        bullets: [
            defineMessage({
                defaultMessage: 'Connect your Function Health account to Perplexity',
                id: '2c31a6eed3',
                description: 'Feature bullet point for the Function Health connector',
            }),
            defineMessage({
                defaultMessage: 'Get personalized health insights and recommendations powered by your health data',
                id: 'a2ee76764b',
                description: 'Feature bullet point for the Function Health connector about insights',
            }),
            defineMessage({
                defaultMessage: 'Easily manage your connection and revoke access at any time',
                id: '9d240f161e',
                description: 'Data access disclosure for the Function Health connector',
            }),
        ],
        developer: PERPLEXITY_DEVELOPER,
        websiteUrl: 'https://www.functionhealth.com',
        documentationUrl: null,
        supportUrl: null,
        footnote: null,
    },
};
//# sourceMappingURL=connectorDetails.js.map